import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from '@tanstack/react-router'
import { ExternalLink, Undo2, X } from 'lucide-react'
import { useSettings } from '@/db/hooks'
import { mutate } from '@/runtime'
import { Button } from '@/components/ui'
import { deckDot, deckFlash, deckTimer } from '@/lib/colour'
import { BATCH, type Answered } from '@/lib/learnTest'
import { tick } from '@/lib/haptics'
import { isExerciseCard, type DeckCard } from '@/lib/learnDeck'
import { answerCard, type Answer, type Outcome } from '@/lib/learnSession'
import { undoLast } from '@/lib/undo'
import { useDeck } from '@/state/deck'
import { CardBody, Tiles } from './cards'
import { useStartDeck, useStartTest } from './parts'
import { TestSummary } from './TestSummary'

const FLASH_MS = 520
/** a question's reason is a sentence: it stays long enough to read, and a tap moves on */
const READ_MS = 2200
const SWIPE = 70
export const DEFAULT_TIMER_S = 15

const linkOf = (card: DeckCard) =>
  card.format === 'lesson'
    ? card.lesson.spec.url
    : card.format === 'concept'
      ? card.concept.spec.source_url
      : card.format === 'learn'
        ? card.concepts.find((c) => c.spec.source_url)?.spec.source_url
        : undefined

const openLink = (card: DeckCard) => {
  const url = linkOf(card)
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

/** Seconds a quick pick may take: its own limit, else Learn settings (15 unless set; 0 is off). */
const timerFor = (card: DeckCard, setting: number | undefined, test?: boolean): number => {
  if (card.format !== 'quick_pick') return 0
  // a test has its own switch: on means the setting, or the default when the setting is off
  if (test !== undefined) return !test ? 0 : (card.exercise.spec.time_limit_s ?? (setting || DEFAULT_TIMER_S))
  return card.exercise.spec.time_limit_s ?? setting ?? DEFAULT_TIMER_S
}

/**
 * The deck: one card at a time, dots along the top, never a count of what is left. Every answer is
 * graded by the exercise and saved at once (lib/learnSession.ts), Undo takes back the last card, and
 * the end is a short word on what went right. Full screen, no scrolling; a card is one idea you can
 * read in under ten seconds.
 */
export const DeckView = () => <DeckPlay key={useDeck((s) => s.sessionId) ?? 'none'} />

const DeckPlay = () => {
  const deck = useDeck((s) => s.deck)
  const sessionId = useDeck((s) => s.sessionId)
  const minutes = useDeck((s) => s.minutes)
  const clear = useDeck((s) => s.clear)
  const settings = useSettings()
  const navigate = useNavigate()
  const startDeck = useStartDeck()
  const startTest = useStartTest()
  const [stopped, setStopped] = useState(false)
  const [noMore, setNoMore] = useState(false)
  const [index, setIndex] = useState(0)
  const [results, setResults] = useState<Outcome[]>([])
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [flipped, setFlipped] = useState(false)
  const [picked, setPicked] = useState<number[]>([])
  const [drag, setDrag] = useState({ x: 0, y: 0 })
  const [lotDone, setLotDone] = useState(false)
  const busy = useRef(false)
  const from = useRef<{ x: number; y: number } | null>(null)

  const cards = deck?.cards ?? []
  const test = deck?.test
  const endless = !!test && test.setup.length === 0
  const finished =
    !!deck &&
    (test ? stopped || (!endless && results.length >= cards.length) : results.length >= cards.length)
  const card = cards[index]
  const timerS = card ? timerFor(card, settings.learn?.timer_s, test?.setup.timer) : 0

  // Endless: a few more questions a little before the end
  useEffect(() => {
    if (endless && test && index >= cards.length - 3) useDeck.getState().extend(test.draw.next(BATCH))
  }, [endless, test, index, cards.length])

  const advance = () => {
    setOutcome(null)
    setFlipped(false)
    setPicked([])
    setIndex((i) => i + 1)
    busy.current = false
  }

  // the outcome holds for a moment (longer when it carries a reason to read), then the next card comes
  useEffect(() => {
    if (!outcome) return
    const t = setTimeout(
      advance,
      card && isExerciseCard(card) && card.format !== 'learn' ? READ_MS : FLASH_MS,
    )
    return () => clearTimeout(t)
    // advance only sets state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  // the quick pick's soft timer: when it runs out the answer is shown and the card comes back later
  useEffect(() => {
    if (!timerS || outcome || finished || !sessionId) return
    const t = setTimeout(() => void answer({ kind: 'timeout' }), timerS * 1000)
    return () => clearTimeout(t)
    // a new card (or a card coming back from undo) starts its own timer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, timerS, outcome, finished, sessionId])

  if (!deck || !sessionId) {
    return (
      <Shell>
        <div className="m-auto text-center px-6" data-testid="deck-empty">
          <p className="text-muted text-sm mb-4">There is no deck open.</p>
          <Button variant="primary" big onClick={() => void navigate({ to: '/learn' })}>
            Back to Learn
          </Button>
        </div>
      </Shell>
    )
  }

  async function answer(a: Answer) {
    if (!card || busy.current || !sessionId) return
    busy.current = true
    tick()
    setDrag({ x: 0, y: 0 })
    try {
      const out = await answerCard(sessionId, card, a, !endless && index === cards.length - 1)
      setResults((r) => [...r, out])
      setOutcome(out)
    } catch {
      busy.current = false
    }
  }

  const undo = async () => {
    // an answer still on screen can be undone at once; one being saved cannot
    if ((busy.current && !outcome) || !results.length || stopped) return
    busy.current = true
    try {
      if (await undoLast()) {
        tick()
        setResults((r) => r.slice(0, -1))
        setIndex(results.length - 1)
        setFlipped(false)
        setPicked([])
        setOutcome(null)
      }
    } finally {
      busy.current = false
    }
  }

  const stop = async () => {
    // a test stopped part way ends here and shows what it found; it is a summary, not a leaving
    if (test && !finished && results.length) {
      busy.current = true
      await mutate.action(sessionId!, 'end')
      setOutcome(null)
      setStopped(true)
      return
    }
    clear()
    void navigate({ to: '/learn' })
  }

  const oneMore = async () => {
    const before = useDeck.getState().sessionId
    if (await startDeck(minutes)) {
      // startDeck has put the new deck in the store; begin it from its first card
      if (useDeck.getState().sessionId !== before) {
        setIndex(0)
        setResults([])
        setFlipped(false)
        setPicked([])
        setLotDone(false)
        return
      }
    }
    setLotDone(true)
  }

  const again = async (missed: boolean) => {
    if (!test) return
    const miss = missed ? answered.filter((a) => a.result !== 'right').map((a) => a.card) : undefined
    if (!(await startTest(test.setup, miss))) setNoMore(true)
  }
  const answered: Answered[] = results.map((r, i) => ({
    card: cards[i] as Answered['card'],
    result: r.result,
  }))

  const swipes = !!card && !isTiles(card)
  const yes = (c: DeckCard): Answer =>
    c.format === 'lesson'
      ? { kind: 'done' }
      : c.format === 'judge'
        ? { kind: 'pick', picked: [0] }
        : { kind: 'got' }
  const no = (c: DeckCard): Answer =>
    c.format === 'lesson'
      ? { kind: 'later' }
      : c.format === 'judge'
        ? { kind: 'pick', picked: [1] }
        : { kind: 'again' }

  const down = (e: PointerEvent) => {
    from.current = { x: e.clientX, y: e.clientY }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const move = (e: PointerEvent) => {
    if (!from.current || busy.current || !swipes) return
    setDrag({ x: e.clientX - from.current.x, y: e.clientY - from.current.y })
  }
  const up = () => {
    const start = from.current
    from.current = null
    if (!start || !card) return
    if (outcome) return advance() // a tap while the answer is up moves on
    if (busy.current) return
    const { x, y } = drag
    if (Math.abs(x) < 8 && Math.abs(y) < 8) {
      if (card.format === 'learn' || card.format === 'concept') setFlipped((f) => !f)
      setDrag({ x: 0, y: 0 })
      return
    }
    if (!swipes) return setDrag({ x: 0, y: 0 })
    if (y < -SWIPE && Math.abs(y) > Math.abs(x)) {
      tick()
      setDrag({ x: 0, y: 0 })
      openLink(card)
    } else if (x > SWIPE) void answer(yes(card))
    else if (x < -SWIPE) void answer(no(card))
    else setDrag({ x: 0, y: 0 })
  }

  const tile = (i: number) => {
    if (!card || !isExerciseCard(card) || outcome || busy.current) return
    if (card.exercise.spec.multi_select)
      setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]))
    else void answer({ kind: 'pick', picked: [i] })
  }

  const locked = !!outcome
  return (
    <Shell>
      <header className="flex items-center gap-2 px-3 pt-2 shrink-0">
        <Button
          variant="quiet"
          big
          className="w-11 px-0"
          onClick={() => void undo()}
          disabled={!results.length || stopped}
          aria-label="Undo"
          data-testid="deck-undo"
        >
          <Undo2 size={20} />
        </Button>
        <div className="flex-1 flex items-center justify-center gap-1.5" data-testid="deck-dots" aria-hidden>
          {cards.map((c, i) =>
            test && (i < index - 4 || i > index + (endless ? 2 : 5)) ? null : (
              <span
                key={`${c.id}:${i}`}
                data-state={i < results.length ? 'done' : i === index ? 'here' : 'ahead'}
                className={`h-1.5 rounded-full transition-all ${
                  i < results.length ? deckDot.done : i === index ? deckDot.here : deckDot.ahead
                } ${i === index && !finished ? 'w-5' : 'w-1.5'}`}
              />
            ),
          )}
        </div>
        <Button
          variant="quiet"
          big
          className="w-11 px-0"
          onClick={() => void stop()}
          aria-label="Close"
          data-testid="deck-close"
        >
          <X size={20} />
        </Button>
      </header>

      {test && !endless && !finished && (
        <div className="mx-4 mt-2 h-px bg-line relative shrink-0" data-testid="test-bar" aria-hidden>
          <span
            className="absolute left-0 -top-px h-[3px] bg-accent transition-all"
            style={{ width: `${(results.length / Math.max(1, cards.length)) * 100}%` }}
          />
        </div>
      )}
      {finished && test ? (
        <TestSummary
          answered={answered}
          none={noMore}
          onAgain={() => void again(false)}
          onMissed={() => void again(true)}
          onDone={() => {
            clear()
            void navigate({ to: '/learn' })
          }}
        />
      ) : finished ? (
        <Summary
          results={results}
          lotDone={lotDone}
          onStop={() => void stop()}
          onMore={() => void oneMore()}
        />
      ) : (
        card && (
          <>
            {timerS > 0 && !outcome && (
              <div
                className="mx-4 mt-2 h-1 rounded-full bg-line overflow-hidden"
                data-testid="deck-timer"
                aria-hidden
              >
                <div
                  key={`${index}-${results.length}`}
                  className={`h-full ${deckTimer}`}
                  style={{ animation: `deck-bar ${timerS}s linear forwards` }}
                />
              </div>
            )}
            <div className="flex-1 min-h-0 flex items-center px-4 py-3">
              <div
                data-testid="deck-card"
                data-format={card.format}
                className={`relative w-full max-h-full overflow-hidden rounded-[var(--radius-card)] border border-line bg-panel px-5 py-6 select-none transition-colors duration-150 ${
                  swipes ? 'touch-none' : ''
                } ${outcome ? deckFlash[outcome.tone] : ''}`}
                style={{
                  transform: `translate(${drag.x}px, ${Math.min(0, drag.y)}px) rotate(${drag.x / 40}deg)`,
                  transition: drag.x || drag.y ? 'none' : undefined,
                }}
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerCancel={() => {
                  from.current = null
                  setDrag({ x: 0, y: 0 })
                }}
              >
                <CardBody card={card} flipped={flipped} />
                <p
                  className="mt-4 min-h-12 text-[15px] leading-6 text-fg-2"
                  aria-live="polite"
                  data-testid="deck-reason"
                >
                  {outcome?.reason ?? ''}
                </p>
                {linkOf(card) && !outcome && (
                  <button
                    type="button"
                    className="absolute right-2 top-2 w-11 h-11 flex items-center justify-center text-faint"
                    aria-label="Open the link"
                    data-testid="deck-open"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => openLink(card)}
                  >
                    <ExternalLink size={18} />
                  </button>
                )}
              </div>
            </div>
            <footer className="px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shrink-0 flex flex-col gap-2.5">
              {isExerciseCard(card) && (card.format === 'quick_pick' || card.format === 'spot') ? (
                <>
                  <Tiles
                    card={card}
                    seed={sessionId}
                    picked={picked}
                    outcome={outcome}
                    disabled={locked}
                    onTile={tile}
                  />
                  {card.exercise.spec.multi_select && (
                    <Button
                      variant="primary"
                      big
                      className="min-h-12"
                      disabled={locked || !picked.length}
                      onClick={() => void answer({ kind: 'pick', picked })}
                      data-testid="deck-check"
                    >
                      Check
                    </Button>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Footer card={card} locked={locked} onAnswer={(a) => void answer(a)} />
                </div>
              )}
            </footer>
          </>
        )
      )}
    </Shell>
  )
}

const isTiles = (c: DeckCard) => c.format === 'quick_pick' || c.format === 'spot'

/** The two buttons of a card that is answered by swiping (and by these, for everyone who does not). */
const Footer = ({
  card,
  locked,
  onAnswer,
}: {
  card: DeckCard
  locked: boolean
  onAnswer: (a: Answer) => void
}): ReactNode => {
  const [no, yes, noId, yesId]: [string, string, string, string] =
    card.format === 'lesson'
      ? ['Not now', 'Mark done', 'deck-later', 'deck-done']
      : card.format === 'judge'
        ? ['False', 'True', 'deck-false', 'deck-true']
        : ['Show again soon', 'Got it', 'deck-again', 'deck-got']
  const noA: Answer =
    card.format === 'lesson'
      ? { kind: 'later' }
      : card.format === 'judge'
        ? { kind: 'pick', picked: [1] }
        : { kind: 'again' }
  const yesA: Answer =
    card.format === 'lesson'
      ? { kind: 'done' }
      : card.format === 'judge'
        ? { kind: 'pick', picked: [0] }
        : { kind: 'got' }
  return (
    <>
      <Button big className="min-h-12" disabled={locked} onClick={() => onAnswer(noA)} data-testid={noId}>
        {no}
      </Button>
      <Button
        variant="primary"
        big
        className="min-h-12"
        disabled={locked}
        onClick={() => onAnswer(yesA)}
        data-testid={yesId}
      >
        {yes}
      </Button>
    </>
  )
}

// a portal, so nothing in the page (its stacking, the tab bar) sits over the deck
const Shell = ({ children }: { children: ReactNode }) =>
  createPortal(
    <div
      className="fixed inset-0 z-50 bg-ink flex flex-col overflow-hidden pt-[env(safe-area-inset-top)]"
      data-testid="deck"
    >
      {children}
    </div>,
    document.body,
  )

/** The end: a short word on what went right. "Stop here" is the big one. */
const Summary = ({
  results,
  lotDone,
  onStop,
  onMore,
}: {
  results: Outcome[]
  lotDone: boolean
  onStop: () => void
  onMore: () => void
}) => {
  const wins = results.flatMap((r) => (r.win ? [r.win] : []))
  const concepts = [...new Set(wins.filter((w) => w.kind === 'concept').map((w) => w.name))]
  const lessons = wins.filter((w) => w.kind === 'lesson').map((w) => w.name)
  const right = results.filter((r) => r.result === 'right').length
  const lines: string[] = []
  if (concepts.length)
    lines.push(
      `You got ${concepts.slice(0, 3).join(', ')}${concepts.length > 3 ? ` and ${concepts.length - 3} more` : ''}.`,
    )
  if (right) lines.push(right === 1 ? 'One question right.' : `${right} questions right.`)
  if (lessons.length)
    lines.push(lessons.length === 1 ? `${lessons[0]} is done.` : `${lessons.length} lessons done.`)
  if (!lines.length) lines.push('You showed up and looked. That counts.')
  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col justify-center px-6" data-testid="deck-summary">
        <h1 className="text-[28px] leading-9 font-semibold mb-4">Nicely done.</h1>
        {lines.map((l) => (
          <p key={l} className="text-[19px] leading-7 text-fg-2 mb-1">
            {l}
          </p>
        ))}
      </div>
      <footer className="flex flex-col items-center gap-2 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shrink-0">
        <Button
          variant="primary"
          big
          className="w-full min-h-14 text-[17px]"
          onClick={onStop}
          data-testid="deck-stop"
        >
          Stop here
        </Button>
        {lotDone ? (
          <p className="text-sm text-muted min-h-11 flex items-center" data-testid="deck-lot">
            That is the lot for now.
          </p>
        ) : (
          <Button variant="quiet" className="min-h-11" onClick={onMore} data-testid="deck-more">
            One more deck
          </Button>
        )}
      </footer>
    </>
  )
}
